import type Geometry from 'ol/geom/Geometry';
import { transformExtent } from 'ol/proj';

/**
 * Coverage inventory of the user's own data against the local DEM, computed in the browser: nothing is uploaded.
 * It answers «which estates/objects fall outside the elevation clip, and which 1°×1° GLO-30 tiles would be needed».
 */
/** What one counted geometry is: Maestro geometries are physical fragments, not fincas, suertes or producers. */
export type CoverageUnit = 'fragments' | 'objects';
export interface CoverageLayerInput { readonly id: string; readonly name: string; readonly unit?: CoverageUnit; readonly geometries: readonly Geometry[] }

export interface LayerCoverage {
  readonly id: string;
  readonly name: string;
  readonly unit: CoverageUnit;
  readonly total: number;
  /** Bounding box fully inside the DEM coverage. */
  readonly inside: number;
  /** Bounding box crosses the coverage limit. */
  readonly partial: number;
  readonly outside: number;
}

/**
 * A CANDIDATE tile: one whose 1° cell overlaps (positive area) the bounding box of at least one object, or contains a
 * point object. It says nothing about whether a given object is covered; that is the inside/partial/outside count.
 */
export interface TileNeed {
  /** Copernicus tile name, e.g. "N11 W086" (south-west corner of the 1° cell). */
  readonly tile: string;
  readonly objects: number;
  /** Some part of this tile is in the local clip (not that the whole tile, or a given object, is covered). */
  readonly partlyInClip: boolean;
}

export interface CoverageInventory {
  readonly layers: readonly LayerCoverage[];
  readonly tiles: readonly TileNeed[];
  /** WGS84 bounding box of everything inventoried: [west, south, east, north]. */
  readonly extent: readonly [number, number, number, number] | null;
}

type Extent = readonly [number, number, number, number];

/** Copernicus 1° tile containing (lon, lat): named after its south-west corner. */
export function copernicusTileName(lon: number, lat: number): string {
  const south = Math.floor(lat); const west = Math.floor(lon);
  return `${south < 0 ? 'S' : 'N'}${String(Math.abs(south)).padStart(2, '0')} ${west < 0 ? 'W' : 'E'}${String(Math.abs(west)).padStart(3, '0')}`;
}

const intersects = (a: Extent, b: Extent): boolean => a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
/** Overlap with positive area: a tile that only touches the clip along its edge is not covered by it. */
const overlaps = (a: Extent, b: Extent): boolean => a[0] < b[2] && a[2] > b[0] && a[1] < b[3] && a[3] > b[1];
/**
 * Integer degree cells spanned by [min, max] with positive length: a box ending exactly on a degree line does not
 * reach the next cell. A zero-length span (a point, or a vertical/horizontal line) uses the cell that contains it,
 * by the same floor rule that names Copernicus tiles.
 */
const cells = (min: number, max: number): number[] => {
  const first = Math.floor(min); const last = max > min ? Math.ceil(max) - 1 : first;
  return Array.from({ length: Math.max(0, last - first) + 1 }, (_, index) => first + index);
};
const contains = (outer: Extent, inner: Extent): boolean => inner[0] >= outer[0] && inner[2] <= outer[2] && inner[1] >= outer[1] && inner[3] <= outer[3];

export function inventoryCoverage(inputs: readonly CoverageLayerInput[], coverage: Extent): CoverageInventory {
  const tiles = new Map<string, { objects: number; partlyInClip: boolean }>();
  let extent: [number, number, number, number] | null = null;
  const layers = inputs.map(({ id, name, unit = 'objects', geometries }) => {
    let inside = 0; let partial = 0; let outside = 0; let total = 0;
    geometries.forEach((geometry) => {
      const raw = geometry.getExtent();
      if (!raw.every(Number.isFinite)) return;
      const box = transformExtent(raw, 'EPSG:3857', 'EPSG:4326') as [number, number, number, number];
      total += 1;
      extent = extent ? [Math.min(extent[0], box[0]), Math.min(extent[1], box[1]), Math.max(extent[2], box[2]), Math.max(extent[3], box[3])] : [...box];
      if (contains(coverage, box)) inside += 1; else if (intersects(coverage, box)) partial += 1; else outside += 1;
      const seen = new Set<string>();
      for (const lat of cells(box[1], box[3])) {
        for (const lon of cells(box[0], box[2])) {
          const tile = copernicusTileName(lon, lat); if (seen.has(tile)) continue; seen.add(tile);
          const entry = tiles.get(tile) ?? { objects: 0, partlyInClip: overlaps(coverage, [lon, lat, lon + 1, lat + 1]) };
          entry.objects += 1; tiles.set(tile, entry);
        }
      }
    });
    return { id, name, unit, total, inside, partial, outside };
  }).filter(({ total }) => total > 0);
  return {
    layers,
    tiles: [...tiles.entries()].map(([tile, value]) => ({ tile, ...value })).sort((a, b) => b.objects - a.objects || a.tile.localeCompare(b.tile)),
    extent,
  };
}
